// A buffer for a progressively rendered image. The buffer contains the whole image, and smaller subsections of the images (tiles) are rendered independently than added to the image
export class ImageBuffer {
  // The image's size
  size: [number, number] = [0,0];
  // The final image buffer. Each pixel has four entries (RGBA -- Same layout as webgl)
  image: Float32Array = new Float32Array(0);
  // The tiling factor. This is the number of tiles in each dimension.
  tiling: number = 1;
  // The tile buffer that webgl should render into
  tile: Float32Array = new Float32Array(0);
  // RGBA image data (suitable for display through ImageData)
  imageData: Uint8ClampedArray = new Uint8ClampedArray(0);

  // Current tile offset
  curTile: [number, number] = [0, 0];
  // Number of samples per pixel (not including the current pass of tiles)
  samples: number = 0;

  constructor(size: [number, number], tiling: number) {
    this.size = size;
    this.image = new Float32Array(size[0] * size[1] * 4);
    this.imageData = new Uint8ClampedArray(size[0] * size[1] * 4);
    this.tiling = tiling;
    this.tile = new Float32Array((size[0] / tiling) * (size[1] / tiling) * 4);
    this.curTile = [0, 0];
    this.samples = 0;
  }

  // Advance to the next tile
  private advanceTile() {
    // advance x
    this.curTile[0]++;
    // advance y if end of row
    if(this.curTile[0] >= this.tiling) {
      this.curTile[1]++;
      this.curTile[0] = 0;
    }
    // advance to next sample if done with all tiles
    if(this.curTile[1] >= this.tiling) {
      this.curTile = [0, 0];
      this.samples++;
    }
  }

  // Add this.tile to image
  addTile() {
    if(isNaN(this.tile[0])) {
      return;
    }
    const tileSize = [this.size[0] / this.tiling, this.size[1] / this.tiling];
    for(let y = 0; y < tileSize[1]; y++) {
      for(let x = 0; x < tileSize[0]; x++) {
        const imageOffset = (((y * this.tiling) + this.curTile[1]) * this.size[0] + (x * this.tiling) + this.curTile[0]) * 4;
        const tileOffset = ((y * tileSize[0]) + x) * 4;
        for(let comp = 0; comp < 4; comp++) {
          this.image[imageOffset + comp] += this.tile[tileOffset + comp];
        }
      }
    }

    this.advanceTile();
  }

  // Clear the buffer
  clear() {
    for(let i = 0; i < this.size[0] * this.size[1] * 4; i++) {
      this.image[i] = 0;
    }
    this.curTile = [0, 0];
    this.samples = 0;
  }

  // Check if curTile is after tileLoc (this means we have rendered tileLoc)
  private tileReached(tileLoc: [number, number]) {
    // check y
    if(this.curTile[1] < tileLoc[1]) {
      return false;
    }
    // check x
    if(this.curTile[1] === tileLoc[1] && this.curTile[0] <= tileLoc[0]) {
      return false;
    }
    return true;
  }


  // Write the image into imageData in RGBA format. The array is suitable for display in an ImageData object
  toRGBArray() {
    for(let y = 0; y < this.size[1]; y++) {
      for(let x = 0; x < this.size[0]; x++) {
        const imageOffset = ((y * this.size[0]) + x) * 4;
        const tileOffset: [number, number] = [x % this.tiling, y % this.tiling];
        const samples = this.tileReached(tileOffset) ? this.samples + 1 : this.samples;

        // if we don't have a value for this pixel, use the 0,0 tile pixel (which is alway rendered first)
        const sampleOffset = samples !== 0 ? 
          imageOffset : 
          ((Math.floor(y / this.tiling) * this.tiling * this.size[0]) + (Math.floor(x / this.tiling) * this.tiling)) * 4;

        for(let comp = 0; comp < 4; comp++) {
          this.imageData[imageOffset + comp] = Math.floor(this.image[sampleOffset + comp] * 255 / Math.max(samples, 1));
        }
      }
    }
  }

  // Draw the image to a canvas
  draw(ctx: CanvasRenderingContext2D) {
    this.toRGBArray();
    const imageData = new ImageData(this.imageData, this.size[0], this.size[1]);
    ctx.putImageData(imageData, 0, 0);
  }

  // Return the number of samples that have been completed
  samplesComplete(): number {
    return this.samples;
  }

  // Return how many tiles are complete in this pass out of total tiles
  sampleTileProgress(): [number, number] {
    return [this.curTile[1] * this.tiling + this.curTile[0], this.tiling * this.tiling];
  }
}
